import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useTheme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import type { SuggestionData } from 'ckeditor5-premium-features'
import clsx from 'clsx'
import { PROPERTY_EDITOR_PILL_STATE_VARIANT } from 'components/WorkspaceEditor/features/pillSelection'
import { useOptionGroupStyles } from 'components/WorkspaceEditor/styles'
import { DataNode } from 'components/WorkspaceEditor/types'
import {
    getDisabledStyleClasses,
    isFirefox,
} from 'components/WorkspaceEditor/utils'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import type { ContentEditorProps } from '../ContentEditor'
import {
    extractSuggestions,
    isKeyedContentForContentItem,
} from '../ContentEditor/contentUtils'
import { withLazyLoad } from '../withLazyComponent'
import { useFormatReplacementOption } from './hooks/useFormatReplacementOption'
import { Hyperlink } from './Hyperlink'
import { useReplacementOptionController } from './useReplacementOptionController'

/** Props for the ReplacementOption component */
interface ReplacementOptionProps {
    /** The content item containing the text and metadata for the replacement option */
    contentItem: Partial<TextContentElement>

    /** Optional CSS classes to apply custom styling */
    classes?: string | Record<string, string>

    /** Component type to render (for inline usage, use 'span' to avoid nesting warnings) */
    component?: React.ElementType

    /** Whether the replacement option is in an inactive state (disabled) */
    inactive?: boolean

    /** Whether the component is disabled (not hidden, just styled as disabled) */
    disabled?: boolean

    /** Whether the component allows text editing */
    editable?: boolean

    /** Optional React key for list rendering optimization */
    selector?: string

    /**
     * Callback function called when the content value changes.
     * @param updatedItem - The updated content item with new values
     * @param isSelected - Whether the item is currently selected
     */
    onValueChange?: (
        updatedItem: Partial<TextContentElement>,
        isSelected: boolean
    ) => void

    /** Optional maximum character length for the input. When set, input is truncated to this length. */
    maxLength?: number

    /** Optional maximum special character count. When set, prevents input of special chars beyond this limit. */
    maxSpecialChars?: number

    /**
     * When true, inactive option styling is skipped to prevent strikethrough on custom insert fields that have actual content.
     */
    isCustomManufacturer?: boolean

    /** When true, clearing the field to empty is treated as a valid change (e.g., drawing designation removal). */
    allowEmpty?: boolean

    /** Parent node level pill selection state fallback (for custom manufacturer payloads) */
    isPillSelected?: boolean

    /** Enables dynamic custom-manufacturer selected styling (flag + cloned family gated by caller) */
    enableDynamicManufacturerSelection?: boolean
    isCustomMFR?: boolean
    node?: DataNode
}

const ContentEditor = withLazyLoad<ContentEditorProps>(() =>
    import('../ContentEditor').then((module) => ({
        default: module.ContentEditor,
    }))
)

/**
 * ReplacementOption Component
 *
 * Renders a replacement option that can display both static and editable content.
 * When editable, it integrates with CKEditor for rich text editing capabilities.
 * Supports Units of Measure handling and maintains consistency with project settings.
 *
 * Features:
 * - Static text display with proper styling
 * - Rich text editing with CKEditor integration
 * - Units of Measure processing and formatting
 * - Focus management and keyboard navigation
 * - Optimistic updates with rollback capabilities
 *
 * @param props - The component props
 * @returns JSX element representing the replacement option
 */
export const ReplacementOption = memo<ReplacementOptionProps>(
    ({
        contentItem,
        classes,
        inactive = false,
        disabled = false,
        editable = false,
        selector,
        onValueChange,
        component = 'p',
        maxLength,
        maxSpecialChars,
        isCustomManufacturer = false,
        allowEmpty = false,
        isPillSelected = false,
        enableDynamicManufacturerSelection = false,
        isCustomMFR,
        node,
    }) => {
        const theme = useTheme()
        const {
            editableText,
            editableTextBoxed,
            disabledBoxedInputContainer,
            notDisabledBoxedInputContainer,
            replacementOptionNoBackground,
            defaultCursor,
            disabledOptionGroup,
            inactivatedOptionItem,
            optionItem,
            selectedOptionItem,
        } = useOptionGroupStyles()
        const {
            setTextContentElementToAdd,
            textContentElementToAdd,
        } = useShallowSelector((state) => ({
            setTextContentElementToAdd:
                state.actions.setTextContentElementToAdd,
            textContentElementToAdd: state.textContentElementToAdd,
        }))
        // Initialize controller for handling editor events and state management
        const {
            handlers,
            ckeToggle,
            setOriginalState,
        } = useReplacementOptionController(contentItem, onValueChange, {
            allowEmpty,
        })

        // Extract content item ID for tracking and reference
        const contentItemId = contentItem.id || ''

        // Generate unique test ID for component identification in tests
        const testId = `replacement-option-${contentItem.id || 'default'}`

        // Format text content with child elements and UOM handling
        const { formattedText } = useFormatReplacementOption(contentItem)

        // Establish content display priority: formatted text > original text > placeholder text > empty string
        // This ensures we always have displayable content even if formatting fails
        const displayText =
            formattedText ||
            contentItem.text?.trim() ||
            contentItem.placeholderText ||
            ''

        // For CKEditor, only use actual content (not placeholder) to avoid treating placeholder as real value
        const editorText = formattedText || contentItem.text?.trim() || ''

        // Simple HTML wrapper for CKEditor - no memoization needed for basic string template
        const initialEditorData = `<p>${editorText}</p>`

        // Extract suggestions for track changes functionality
        const initialSuggestions: SuggestionData[] = useMemo(
            () => extractSuggestions([contentItem]),
            [contentItem.id, contentItem.changeType]
        )
        // Extract option item state for disabled styling only
        const isInActiveOptionItem: boolean = useMemo(
            () =>
                !isCustomManufacturer &&
                contentItem?.optionItemState ===
                    PROPERTY_EDITOR_PILL_STATE_VARIANT.InActive,
            [contentItem.optionItemState, isCustomManufacturer]
        )

        // Check if this is an OptedIn element (should show as pill when not active)
        const isOptedIn =
            contentItem?.optionItemState ===
            PROPERTY_EDITOR_PILL_STATE_VARIANT.OptedIn

        // Dynamic custom manufacturer selection is gated by feature flag + cloned family context.
        // Don't apply selected styling if under an inactive paragraph.
        const isCustomManufacturerSelected =
            isCustomManufacturer &&
            !inactive &&
            enableDynamicManufacturerSelection &&
            ((contentItem as Partial<TextContentElement> & {
                isSelected?: boolean
            }).isSelected === true ||
                isPillSelected)

        const { active, placeholderRef } = ckeToggle

        const [clickCoordinates, setClickCoordinates] = useState<
            { x: number; y: number } | undefined
        >()

        // Activation handler: prevents Firefox mousedown race condition,
        // captures coordinates for cursor positioning, and delegates to controller.
        const handleActivationClick = useCallback(
            (e: React.MouseEvent<HTMLElement>) => {
                if (active) return

                // Firefox: prevent mousedown to avoid focus race during DOM replacement
                if (isFirefox && e.type === 'mousedown') {
                    e.preventDefault()
                    return
                }

                if (e.clientX || e.clientY) {
                    setClickCoordinates({ x: e.clientX, y: e.clientY })
                    setTimeout(() => setClickCoordinates(undefined), 200)
                }

                ;(handlers.onFocusCapture as () => void)()
            },
            [active, handlers.onFocusCapture]
        )

        // Show selected styling when OptedIn (or selected custom manufacturer) and not actively editing
        const showAsPill =
            (isOptedIn || isCustomManufacturerSelected) && !active
        const isInactivePill = isInActiveOptionItem && !active
        const isInteractive = !disabled && !inactive && (editable || showAsPill)

        /**
         * Auto-focus effect for replacement options after opted in Replacement option is clicked.
         *
         * When a ReplacementOption is clicked from OptedIn state,
         * this effect automatically activates the editor to allow immediate editing.
         * The state is cleared immediately after activation to prevent stale references.
         *
         * Race condition prevention: Deactivates all other editors before activating this one
         * to ensure only one ReplacementOption is in edit mode at a time.
         */
        useEffect(() => {
            // Check if this element should be auto-focused (triggered by clicking OptedIn pill)
            if (textContentElementToAdd?.id === contentItemId) {
                // Store the original optionItemState BEFORE clearing textContentElementToAdd
                // This allows reverting if user clicks away without making changes
                if (textContentElementToAdd?.optionItemState) {
                    setOriginalState(textContentElementToAdd.optionItemState)
                }

                // Use the same activation path as a click: deactivates others, registers
                // with FocusTracker, activates editor, and schedules DOM focus.
                ;(handlers.onFocusCapture as () => void)()

                // Clear the state to prevent the effect from running again with stale data
                setTextContentElementToAdd(null)
            }
        }, [
            handlers.onFocusCapture,
            textContentElementToAdd,
            contentItemId,
            setTextContentElementToAdd,
            setOriginalState,
        ])
        /**
         * Determine if the component should be disabled (non-interactive)
         *
         * A replacement option is disabled when any of these conditions are true:
         * - Parent paragraph is inactive (inactive=true)
         * - Component is not editable (editable=false)
         * - Component is explicitly disabled (disabled=true) - used for product groups not added to project
         */
        const isDisabled = inactive || !editable || disabled

        // Get disabled styling classes using shared helper
        const disabledClasses = getDisabledStyleClasses(
            disabled,
            inactive,
            editable,
            {
                disabledOptionGroup,
                disabledBoxedInputContainer,
                notDisabledBoxedInputContainer,
            }
        )

        // Check if we're showing placeholder text (no actual value)
        const isShowingPlaceholder =
            !formattedText &&
            !contentItem.text?.trim() &&
            !!contentItem.placeholderText

        // Memoize style objects to prevent breaking React.memo optimization
        const divContainerStyle = useMemo(
            () => ({ display: 'inline-block' as const }),
            []
        )
        const placeholderStyle = useMemo(
            () => ({
                opacity: 0.5,
                display: 'unset !important' as const,
                whiteSpace: 'nowrap' as const,
                padding: '2px 5px',
            }),
            []
        )

        const defaultStyle = useMemo(
            () => ({
                display: 'unset !important' as const,
                whiteSpace: 'nowrap' as const,
                padding: '2px 5px',
            }),
            []
        )

        // Style for pill appearance (OptedIn state)
        const pillStyle = useMemo(
            () => ({
                display: 'unset !important' as const,
                whiteSpace: 'nowrap' as const,
                padding: '2px 5px',
                color: theme.palette.common.white,
            }),
            [theme.palette.common.white]
        )

        return isCustomMFR ? (
            <Hyperlink
                contentItem={contentItem}
                isKeyedHyperlink={isKeyedContentForContentItem(contentItem)}
                inactive={inactive || node?.contentItemView?.isDeactivated}
                disabled={disabled}
                editable={editable}
                disableCursorOnly={false}
                node={node}
            />
        ) : (
            // This div is necessary to prevent nesting issues with Typography component
            <div style={divContainerStyle}>
                <Typography
                    key={selector || testId}
                    component={component}
                    data-testid={testId}
                    data-suggestion={`content-suggestion-id-${initialSuggestions?.[0]?.id}`}
                    tabIndex={isDisabled || isInActiveOptionItem ? -1 : 0}
                    aria-disabled={isDisabled || isInActiveOptionItem}
                    ref={active ? placeholderRef : null}
                    onMouseDown={
                        isInteractive ? handleActivationClick : undefined
                    }
                    onFocusCapture={
                        isInteractive ? handleActivationClick : undefined
                    }
                    onClick={isInteractive ? handleActivationClick : undefined}
                    className={clsx(
                        classes,
                        showAsPill
                            ? [optionItem, selectedOptionItem, 'optionItem']
                            : isInactivePill
                            ? [optionItem, inactivatedOptionItem, 'optionItem']
                            : [
                                  editableText,
                                  editableTextBoxed,
                                  replacementOptionNoBackground,
                                  'editableTextBoxed',
                              ],
                        !editable && defaultCursor,
                        !(showAsPill || isInactivePill) && disabledClasses
                    )}
                    style={
                        showAsPill
                            ? pillStyle
                            : isShowingPlaceholder && !active
                            ? placeholderStyle
                            : defaultStyle
                    }
                    onKeyDown={
                        isDisabled
                            ? undefined
                            : (event: React.KeyboardEvent<HTMLElement>) =>
                                  handlers.onKeyDown(event)
                    }
                >
                    {active && (editable || isOptedIn || isInActiveOptionItem) && !inactive ? (
                        <ContentEditor
                            initialData={initialEditorData}
                            initialSuggestions={initialSuggestions}
                            handlers={handlers}
                            enableTrackChange={false}
                            maxLength={maxLength}
                            maxSpecialChars={maxSpecialChars}
                            clickCoordinates={clickCoordinates}
                        />
                    ) : (
                        displayText
                    )}
                </Typography>
            </div>
        )
    }
)
